import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileSpreadsheet, FileText, Trash2, Unlock } from 'lucide-react';

import { ApiError } from '@/lib/api';
import {
  adminDeleteFolder,
  folderPdfUrl,
  folderXlsxUrl,
  formatAmount,
  getAdminFolder,
  reopenFolder,
  ticketDownloadUrl,
} from '@/lib/tickets-api';
import { Button } from '@/components/ui/button';

/**
 * Detalle admin: revisa el contenido de la carpeta, descarga PDF chorizo y
 * Excel para contabilidad, reabre si el empleado se dejó algo, y borra si
 * la carpeta es un error.
 */
export function AdminTicketFolder() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const folderQuery = useQuery({
    queryKey: ['admin', 'tickets', 'folder', id],
    queryFn: () => getAdminFolder(id!),
    enabled: !!id,
  });

  const reopen = useMutation({
    mutationFn: () => reopenFolder(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'trips'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'tickets', 'folder', id] });
    },
  });

  const remove = useMutation({
    mutationFn: () => adminDeleteFolder(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'trips'] });
      navigate(-1);
    },
  });

  if (!id) return null;
  if (folderQuery.isLoading) return <p className="p-6 text-sm text-muted-foreground">Cargando…</p>;
  if (folderQuery.error || !folderQuery.data) {
    return (
      <p role="alert" className="p-6 text-sm text-destructive">
        {folderQuery.error instanceof ApiError
          ? folderQuery.error.message
          : 'Error cargando carpeta'}
      </p>
    );
  }

  const folder = folderQuery.data;
  const isClosed = folder.status === 'CLOSED';

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground mb-3"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Volver al viaje
      </button>

      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden mb-5">
        <header className="px-7 py-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">{folder.name}</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {folder.ownerName} · {folder.ticketCount} ticket
              {folder.ticketCount === 1 ? '' : 's'}
              {folder.totalCents > 0 ? ` · Total ${formatAmount(folder.totalCents)}` : ''}
            </p>
            {folder.description ? (
              <p className="text-[13px] text-muted-foreground mt-1">{folder.description}</p>
            ) : null}
            {folder.closedAt ? (
              <p className="text-[12px] text-muted-foreground mt-1">
                Cerrada el {new Date(folder.closedAt).toLocaleString()} por {folder.closedByName ?? '?'}.
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {folder.tickets.length > 0 ? (
              <>
                <Button variant="primary" size="sm" asChild>
                  <a href={folderPdfUrl(folder.id)} target="_blank" rel="noreferrer">
                    <FileText className="h-3.5 w-3.5" />
                    PDF chorizo
                  </a>
                </Button>
                <Button variant="secondary" size="sm" asChild>
                  <a href={folderXlsxUrl(folder.id)} target="_blank" rel="noreferrer">
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    Excel
                  </a>
                </Button>
              </>
            ) : null}
            {isClosed ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm('¿Reabrir la carpeta para que el empleado pueda modificarla?'))
                    reopen.mutate();
                }}
                disabled={reopen.isPending}
              >
                <Unlock className="h-3.5 w-3.5" />
                Reabrir
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('¿Borrar esta carpeta entera? Es irreversible.'))
                  remove.mutate();
              }}
              disabled={remove.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Borrar
            </Button>
          </div>
        </header>

        {folder.tickets.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            La carpeta está vacía.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {folder.tickets.map((t) => (
              <li key={t.id} className="px-5 py-3 flex items-center gap-4">
                <a
                  href={ticketDownloadUrl(t.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0"
                >
                  <img
                    src={ticketDownloadUrl(t.id)}
                    alt={t.title}
                    className="h-16 w-16 object-cover rounded-sm border border-border hover:border-icaria-600"
                  />
                </a>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {t.category ? (
                      <span
                        className="inline-flex items-center font-mono text-[9px] font-medium uppercase border border-icaria-600/40 bg-icaria-50 text-icaria-700 rounded-[3px] px-1.5 py-0.5"
                        style={{ letterSpacing: '0.06em' }}
                      >
                        {t.category}
                      </span>
                    ) : null}
                    <p className="font-medium text-[14.5px] truncate">{t.title}</p>
                  </div>
                  <p className="text-[12.5px] text-muted-foreground truncate">
                    {t.ticketDate ?? 'sin fecha'} · {formatAmount(t.amountCents, t.currency)}
                  </p>
                  {t.notes ? (
                    <p className="text-[12px] text-muted-foreground/80 truncate mt-0.5">
                      {t.notes}
                    </p>
                  ) : null}
                </div>
                <a
                  href={ticketDownloadUrl(t.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-foreground p-2"
                  aria-label="Ver foto"
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(reopen.error || remove.error) && (
        <p role="alert" className="text-sm text-destructive">
          {reopen.error instanceof Error
            ? reopen.error.message
            : remove.error instanceof Error
              ? remove.error.message
              : 'Error en la acción'}
        </p>
      )}
    </div>
  );
}
